import React, { useEffect, useState } from "react";
import NextButton from "../NextButton";
import "../MobileWizard.scss";
import { UserInfo } from "../MobileWizard";
import PickTeamsPanel from "../PickTeamsPanel";
import _isEqual from "lodash/isEqual";
import { Typography, TextField, InputAdornment } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { CircleFlag } from "../../../common/CircleFlag";
import debounce from "debounce";

const MAX_TEAMS = 3;

interface PreferencesStepProps {
  createAccount: () => void;
  setUserInfo: React.Dispatch<React.SetStateAction<UserInfo>>;
  userInfo: UserInfo;
  isDesktop?: boolean;
}

const PreferencesStep: React.FC<PreferencesStepProps> = ({
  createAccount,
  setUserInfo,
  userInfo,
}) => {
  const [chosenTeams, setChosenTeams] = useState<TeamInfo[]>(
    userInfo.preferences
  );
  const [query, setQuery] = useState("");

  function onTeamClick(chosenTeam: TeamInfo) {
    const withoutTeam = chosenTeams.filter((item) => !_isEqual(item, chosenTeam));
    // Already picked → toggle it off.
    if (withoutTeam.length !== chosenTeams.length) {
      setChosenTeams(withoutTeam);
      return;
    }
    // New pick: append while under the cap, otherwise drop the oldest (last) and
    // add the new one at the front. Always build a new array — never mutate state.
    setChosenTeams((prev) =>
      prev.length < MAX_TEAMS
        ? [...prev, chosenTeam]
        : [chosenTeam, ...prev.slice(0, MAX_TEAMS - 1)]
    );
  }

  useEffect(() => {
    setUserInfo((prev) => ({ ...prev, preferences: chosenTeams }));
  }, [chosenTeams, setUserInfo]);

  return (
    <div className="preferencesStep">
      <div className="preferencesStep__top">
        <Typography
          variant="h4"
          className="wizardStep__heading wizardStep__heading--tight"
        >
          Select your favourite teams
        </Typography>
        <Typography className="preferencesStep__subtitle">
          Your choices will personalize your experience, bringing you closer to
          the action and updates that matter to you.
        </Typography>

        {/* Chosen picks — always three slots so the layout never jumps. Filled
            slots are removable chips; empty ones are dashed placeholders. */}
        <div className="favTeams">
          <div className="favTeams__header">
            <span className="favTeams__title">Your picks</span>
            <span className="favTeams__counter">
              {chosenTeams.length}/{MAX_TEAMS}
            </span>
          </div>
          <div className="favTeams__slots">
            {Array.from({ length: MAX_TEAMS }).map((_, index) => {
              const team = chosenTeams[index];
              if (!team) {
                return (
                  <div
                    className="favTeams__slot favTeams__slot--empty"
                    key={`empty-${index}`}
                    aria-hidden="true"
                  >
                    <span className="favTeams__placeholder" />
                    <span className="favTeams__name favTeams__name--muted">
                      Empty
                    </span>
                  </div>
                );
              }
              return (
                <button
                  type="button"
                  className="favTeams__slot"
                  key={team.countryCode}
                  onClick={() => onTeamClick(team)}
                  aria-label={`Remove ${team.name}`}
                >
                  <span className="favTeams__flagWrap">
                    <CircleFlag
                      countryCode={team.countryCode}
                      height={48}
                      width={48}
                      className="favTeams__flag"
                    />
                    <span className="favTeams__remove">
                      <CloseRoundedIcon />
                    </span>
                  </span>
                  <span className="favTeams__name">{team.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        <TextField
          fullWidth
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search teams"
          className="preferencesStep__search"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <div className="preferencesStep__teamsScroll">
        <PickTeamsPanel
          onTeamClick={onTeamClick}
          chosenTeams={chosenTeams}
          query={query}
        />
      </div>

      <div className="preferencesStep__footer">
        <NextButton
          label="Create your account"
          createAccount={debounce(createAccount, 500)}
        />
      </div>
    </div>
  );
};

export default PreferencesStep;

export type TeamInfo = { name: string; countryCode: string };
